type Primitive = string | number | boolean | bigint | symbol | null | undefined | Date;

export interface PatchValue<T> {
  present: boolean;
  value?: T;
}

/**
 * For object fields:
 *  - You can supply a nested PatchDTO<T> (preferred in this mode), OR
 *  - Fall back to a PatchValue<T> (e.g., { present: true } to clear the whole object).
 * For non-objects/arrays: standard PatchValue<T>.
 */
export type PatchField<T> =
  T extends Primitive
    ? PatchValue<T>
    : T extends any[]
      ? PatchValue<T>
      : PatchDTO<T> | PatchValue<T>;

export type PatchDTO<T> = {
  [K in keyof T]?: PatchField<T[K]>;
};

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  Object.prototype.toString.call(v) === '[object Object]';

const looksLikePatchValue = (v: unknown): v is { present: boolean; value?: unknown } =>
  isPlainObject(v) && typeof (v as any).present === 'boolean';

const looksLikePatchDTO = (v: unknown): v is Record<string, { present: boolean; value?: unknown }> =>
  isPlainObject(v) && Object.values(v).every((entry) => {
    // Either a PatchValue or a nested object (which may itself be a PatchDTO)
    if (!isPlainObject(entry)) return false;
    return looksLikePatchValue(entry) || looksLikePatchDTO(entry);
  });

/**
 * Build a PatchDTO from partial input.
 * - Primitives/arrays => PatchValue { present: true, value }
 * - `undefined` => PatchValue { present: true } (useful to clear a field)
 * - Plain objects:
 *     • Become nested PatchDTOs (no wrapper) by **default**.
 *     • If the provided object already looks like a PatchDTO, it’s passed through unchanged.
 *     • If you truly want to replace/clear the whole object, pass the field as `undefined`
 *       (you’ll get `{ present: true }` at that key).
 */
export function buildPatchDto<T>(
  input: Partial<T>,
  fieldMap?: Record<string, string>
): PatchDTO<T> {
  const dto: Partial<Record<keyof T, PatchField<any>>> = {};

  for (const [inputKey, rawValue] of Object.entries(input as Record<string, unknown>)) {
    const targetKey = (fieldMap?.[inputKey] ?? inputKey) as keyof T;

    if (rawValue === undefined) {
      // Signal "field is present but no value" (e.g., clear it)
      dto[targetKey] = {present: true} as PatchField<any>;
      continue;
    }

    // Arrays: standard PatchValue wrapper
    if (Array.isArray(rawValue)) {
      dto[targetKey] = {present: true, value: rawValue} as PatchField<any>;
      continue;
    }

    // If caller already provided a nested PatchDTO at this key, pass it through
    if (looksLikePatchDTO(rawValue)) {
      dto[targetKey] = rawValue as PatchField<any>;
      continue;
    }

    // Plain objects => build a nested PatchDTO (no wrapper at this level)
    if (isPlainObject(rawValue)) {
      dto[targetKey] = buildPatchDto(rawValue as any) as PatchField<any>;
      continue;
    }

    // Primitives => standard PatchValue wrapper
    dto[targetKey] = {present: true, value: rawValue} as PatchField<any>;
  }

  return dto as PatchDTO<T>
}
